<template>
  <div class="flex flex-col gap-10 relative">
    <ContentRow
      v-if="weaknessMods.length > 0"
      :mods="weaknessMods"
      :title="$t('play.play_now.improve_weaknesses')"
      icon="downarrow"
      iconColor="#EF4444"
      @selectMod="selectMod"
      :selectedMod="selectedMod"
      :showCategory="true"
      :modCategories="modCategories"
      :limitMods="true"
    />
    <ContentRow
      v-if="strengthMods.length > 0"
      :mods="strengthMods"
      :title="$t('play.play_now.play_to_strengths')"
      icon="uparrow"
      iconColor="#14B8A6"
      @selectMod="selectMod"
      :selectedMod="selectedMod"
      :showCategory="true"
      :modCategories="modCategories"
      :limitMods="true"
    />

    <!-- Empty state when no coach content and data is loaded -->
    <div v-if="isDataLoaded && weaknessMods.length === 0 && strengthMods.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
      <img
        src="../../assets/icons/coach.svg"
        class="h-12 w-12 opacity-40 mb-4"
        :alt="$t('play.play_now.sparkle_icon_alt')"
      />
      <h3 class="text-lg font-medium text-text-secondary mb-2">{{ $t('play.play_now.for_you_empty_title') }}</h3>
      <p class="text-text-secondary max-w-md">
        {{ $t('play.play_now.for_you_empty_body') }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import ContentRow from './ContentRow.vue';
import { formatStatString } from '../../utils/formatters.js';

const store = useStore();

const props = defineProps({
  selectedMod: {
    type: Object,
    required: true,
  },
  fiveWorstStats: {
    type: Array,
    required: true,
  },
  fiveBestStats: {
    type: Array,
    required: true,
  },
  modCategories: {
    type: Array,
    required: false,
    default: () => [],
  },
  isDataLoaded: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const emit = defineEmits(['selectMod']);

function selectMod(mod) {
  emit('selectMod', mod);
}

// Function to get the auto-selection mod based on priority
function getAutoSelectionMod() {
  // Priority 1: First weakness mod
  if (weaknessMods.value.length > 0) {
    return weaknessMods.value[0];
  }

  // Priority 2: First strength mod
  if (strengthMods.value.length > 0) {
    return strengthMods.value[0];
  }

  // Priority 3: First mod in the first category
  if (mods.value.length > 0) {
    return mods.value[0];
  }

  return null;
}

// Expose the function for parent component
defineExpose({
  getAutoSelectionMod,
});

const mods = computed(() => store.state.modInfo?.mods?.filter((mod) => mod.playable && mod.cs2) || []);

const weaknessMods = computed(() => {
  if (!props.fiveWorstStats || !Array.isArray(props.fiveWorstStats)) {
    return [];
  }

  // Filter to only include actual weaknesses (negative performance) and sort by worst first
  const actualWeaknesses = props.fiveWorstStats
    .filter((stat) => stat.data?.percent_difference && parseFloat(stat.data.percent_difference) < 0)
    .sort((a, b) => parseFloat(a.data.percent_difference) - parseFloat(b.data.percent_difference));

  if (actualWeaknesses.length === 0) {
    return [];
  }

  const selectedModIds = new Set();
  const result = [];
  const maxMods = 5;

  // Cycle through stats and mod indices until we have 5 mods or no more options
  let currentModIndex = 0;

  while (result.length < maxMods) {
    let foundMod = false;

    for (const stat of actualWeaknesses) {
      if (result.length >= maxMods) break;

      if (!stat.data?.recommended_mods || !Array.isArray(stat.data.recommended_mods)) {
        continue;
      }

      // Check if there's a mod at the current index for this stat
      if (currentModIndex < stat.data.recommended_mods.length) {
        const recommendedMod = stat.data.recommended_mods[currentModIndex];

        if (recommendedMod && recommendedMod.modifier) {
          // Find the mod in the mod list that matches the modifier name
          const matchingMod = mods.value?.find((mod) => mod?.name?.toLowerCase() === recommendedMod.modifier.modifier_name?.toLowerCase());

          if (matchingMod && !selectedModIds.has(matchingMod.id)) {
            selectedModIds.add(matchingMod.id);
            // Add the stat information to the mod
            const modWithStat = {
              ...matchingMod,
              recommendedForStat: {
                title: stat.title,
                type: 'weakness',
                displayName: formatStatString(stat.title),
                percentDifference: stat.data.percent_difference,
                isNegative: stat.data.is_negative,
              },
            };
            result.push(modWithStat);
            foundMod = true;
          }
        }
      }
    }

    // If we didn't find any mods in this cycle, move to next mod index
    if (!foundMod) {
      currentModIndex++;
      // If we've exhausted all mod indices, break
      if (currentModIndex >= Math.max(...actualWeaknesses.map((stat) => stat.data?.recommended_mods?.length || 0))) {
        break;
      }
    }
  }

  return result;
});

const strengthMods = computed(() => {
  if (!props.fiveBestStats || !Array.isArray(props.fiveBestStats)) {
    return [];
  }

  // Filter to only include actual strengths (positive performance) and sort by best first
  const actualStrengths = props.fiveBestStats
    .filter((stat) => stat.data?.percent_difference && parseFloat(stat.data.percent_difference) > 0)
    .sort((a, b) => parseFloat(b.data.percent_difference) - parseFloat(a.data.percent_difference));

  if (actualStrengths.length === 0) {
    return [];
  }

  // Get already selected mod IDs from weakness mods to avoid duplicates
  const alreadySelectedIds = new Set(weaknessMods.value.map((mod) => mod.id));
  const result = [];
  const maxMods = 5;

  // Cycle through stats and mod indices until we have 5 mods or no more options
  let currentModIndex = 0;

  while (result.length < maxMods) {
    let foundMod = false;

    for (const stat of actualStrengths) {
      if (result.length >= maxMods) break;

      if (!stat.data?.recommended_mods || !Array.isArray(stat.data.recommended_mods)) {
        continue;
      }

      // Check if there's a mod at the current index for this stat
      if (currentModIndex < stat.data.recommended_mods.length) {
        const recommendedMod = stat.data.recommended_mods[currentModIndex];

        if (recommendedMod && recommendedMod.modifier) {
          // Find the mod in the mod list that matches the modifier name
          const matchingMod = mods.value?.find((mod) => mod?.name?.toLowerCase() === recommendedMod.modifier.modifier_name?.toLowerCase());

          if (matchingMod && !alreadySelectedIds.has(matchingMod.id)) {
            alreadySelectedIds.add(matchingMod.id);
            // Add the stat information to the mod
            const modWithStat = {
              ...matchingMod,
              recommendedForStat: {
                title: stat.title,
                type: 'strength',
                displayName: formatStatString(stat.title),
                percentDifference: stat.data.percent_difference,
                isNegative: stat.data.is_negative,
              },
            };
            result.push(modWithStat);
            foundMod = true;
          }
        }
      }
    }

    // If we didn't find any mods in this cycle, move to next mod index
    if (!foundMod) {
      currentModIndex++;
      // If we've exhausted all mod indices, break
      if (currentModIndex >= Math.max(...actualStrengths.map((stat) => stat.data?.recommended_mods?.length || 0))) {
        break;
      }
    }
  }

  return result;
});

</script>
